import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { InputFormFieldComponent } from '../fields/input-form-field/input-form-field.component';
import { CheckboxFormFieldComponent } from '../fields/checkbox-form-field/checkbox-form-field.component';
import { DatepickerFormFieldComponent } from '../fields/datepicker-form-field/datepicker-form-field.component';
import { Subject, takeUntil } from 'rxjs';
import { HelperService } from 'app/shared/services/helper.service';
import { CaseFormService } from 'app/case-form/case-form.service';
import { CommonModule } from '@angular/common';
import { CaseStatusEnum } from 'app/shared/enums/case-status.enum';
import { RichTextEditorComponent } from '../fields/rich-text-editor/rich-text-editor.component';
import { ConfigurationSelectFormFieldComponent } from '../fields/configuration-select-form-field/configuration-select-form-field.component';
import { IUser } from 'app/shared/models/users.model';
import { DropdownTypeEnum } from 'app/shared/enums/dropdowns.enum';
import { UsersService } from 'app/shared/services/users.service';
import { ConfigurationService } from 'app/shared/services/configuration.service';

@Component({
  selector: 'app-case-resolution-containment-form',
  standalone: true,
  imports: [
    InputFormFieldComponent,
    RichTextEditorComponent,
    CheckboxFormFieldComponent,
    DatepickerFormFieldComponent,
    ConfigurationSelectFormFieldComponent,
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
  ],
  templateUrl: './case-resolution-containment-form.component.html',
  styleUrl: './case-resolution-containment-form.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})

export class CaseResolutionContainmentFormComponent implements OnInit, OnDestroy {
  @Input() viewMode!: boolean;
  @Input() caseStage: string = '';
  @Input() productCompany!: string;
  @Input() productSuite!: string;
  @Input() showVerifiedKnowledge: boolean = false;

  form: any;

  caseContactUsers: IUser[] = [];
  dropdownRootCauseType = DropdownTypeEnum.ROOT_CAUSE_SYSTEM_COMPONENT_COMPLEX;

  errorTextContainmentSummary = '';
  errorTextProblemResolutionSummary = '';
  isProblemResolutionSummaryReqired = false;
  isRootCauseFieldsRequired = false;
  caseStatusEnum = CaseStatusEnum;

  private unsubscribe$ = new Subject<void>();

  constructor(
    public mainForm: FormGroupDirective,
    private helperService: HelperService,
    private caseFormService: CaseFormService,
    private usersService: UsersService,
    private configurationService: ConfigurationService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.getUsers();

    this.caseContainedValueChanges();
    this.containmentSummaryValueChanges();
    this.problemResolutionSummaryValueChanges();
    this.onChangeCaseStageEmiterSubscription();
    this.onChangeProductSuiteAndCompanySubscription();
  }

  getUsers() {
    this.usersService.users$.subscribe((users: IUser[]) => {
      this.caseContactUsers = users;
    });
  }

  caseContainedValueChanges() {
    this.form.controls['caseContained'].valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value: boolean) => {
        this.form.controls['containmentSummary'].setValidators(value ? [Validators.required] : null);
        this.form.get('containmentSummary').updateValueAndValidity();
      });
  }

  containmentSummaryValueChanges() {
    this.form.controls['containmentSummary'].valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.errorTextContainmentSummary = this.helperService.handleFieldsErrors(this.form.get('containmentSummary'));
      });
  }

  problemResolutionSummaryValueChanges() {
    this.form.controls['problemResolutionSummary'].valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.errorTextProblemResolutionSummary = this.helperService.handleFieldsErrors(this.form.get('problemResolutionSummary'));
      });
  }

  onChangeCaseStageEmiterSubscription() {
    this.caseFormService.onChangeCaseStageEmiter
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: any) => {
        this.setFormValidators(data);
      });
  }
  onChangeProductSuiteAndCompanySubscription() {
    this.caseFormService.onChangeProductSuiteAndCompanyEmitter
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((caseStage: any) => {
        this.setFormValidators({ value: caseStage });
      });
  }

  setFormValidators(data: any) {
    const caseStageIsClosed = data.value === this.caseStatusEnum.CLOSED_RESOLVED || data.value === this.caseStatusEnum.CLOSED_NOT_RESOLVED;
    this.isProblemResolutionSummaryReqired = caseStageIsClosed;

    if (caseStageIsClosed) {
      const reqBody = { type: 'RootCauseSystemComponentComplex', entry1: this.form.get('productCompany')?.value, entry2: this.form.get('productSuite')?.value, entry3: '' }
      this.configurationService.getDropdownConfigurations(reqBody, 'entry3').pipe(takeUntil(this.unsubscribe$)).subscribe(res => {
        this.isRootCauseFieldsRequired = res && res.length;
        this.setFieldsValidatorsByCaseStage(caseStageIsClosed);
      });
    } else {
      this.isRootCauseFieldsRequired = false;
      this.setFieldsValidatorsByCaseStage(caseStageIsClosed);
    }
  }

  setFieldsValidatorsByCaseStage(caseStageIsClosed: boolean) {
    this.form.controls['problemResolutionSummary'].setValidators(caseStageIsClosed ? [Validators.required] : null);
    this.form.controls['rootCauseSystemComponent'].setValidators(this.isRootCauseFieldsRequired ? [Validators.required] : null);
    this.form.controls['rootCauseCategory'].setValidators(this.isRootCauseFieldsRequired ? [Validators.required] : null);
    this.form.get('problemResolutionSummary').updateValueAndValidity();
    this.form.get('rootCauseSystemComponent').updateValueAndValidity();
    this.form.get('rootCauseCategory').updateValueAndValidity();
  }

  createForm() {
    this.form = this.mainForm.form;

    this.form.addControl('caseContained', new FormControl(false, []));
    this.form.addControl('containmentSummary', new FormControl(null, []));
    this.form.addControl('caseContainedDate', new FormControl({ value: null, disabled: true }, []));
    this.form.addControl('problemResolutionSummary', new FormControl(null, []));
    this.form.addControl('causeCode', new FormControl(null, []));
    this.form.addControl('correctiveActionOutput', new FormControl(null, []));
    this.form.addControl('rootCauseSystemComponent', new FormControl(null, []));
    this.form.addControl('rootCauseCategory', new FormControl(null, []));
    this.form.addControl('trainingOpportunity', new FormControl(false, []));
    this.form.addControl('trainingComments', new FormControl(null, []));
    this.form.addControl('verifiedKnowledge', new FormControl(false, []));

    this.validationRequiredFields();
  }

  clearFormField(fieldName: string) {
    this.form.get(fieldName)?.setValue(null);
  }

  validationRequiredFields() {
    const isRequiredField =
      this.form.get('caseStage')?.value === this.caseStatusEnum.CLOSED_RESOLVED
      || this.form.get('caseStage')?.value === this.caseStatusEnum.CLOSED_NOT_RESOLVED;
    this.isProblemResolutionSummaryReqired = isRequiredField;
    this.form.controls['problemResolutionSummary'].setValidators(isRequiredField ? [Validators.required] : null);
    this.form.get('problemResolutionSummary').updateValueAndValidity();

    this.form.controls['containmentSummary'].setValidators(this.form.get('caseContained').value ? [Validators.required] : null);
    this.form.get('containmentSummary').updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
